import { OpenSheetMusicDisplay as OSMD, GraphicalNote, VexFlowGraphicalNote, Note } from '@museflow-ai/opensheetmusicdisplay';
import { PrerenderedGraphics, MidiEventState, midiSemiErrorStates, midiFullErrorStates, MidiEventType, ErrorRecognitionActive } from 'Types';
import { makeNoteSemiError, makeNoteError, makeNoteCorrect, resetNote } from 'Utils/OSMDUtility';
import ITimeKeeper from './ITimeKeeper';
import { findAndReplace, isErrorState, isSemiErrorState, sumToN } from 'Utils';
import { sum, range, every, add } from 'lodash';
import { v4 as uuid } from 'uuid';
import Phrase, { Iterator, IteratorPhaser } from './Phrase';
import { COLORBLIND_TYPE, ERROR_REC_CLASS, HANDEDNESS } from 'Utils/Constants';
import logger from 'Utils/Logger';

const onsetDefault = 0.3;
const offsetDefault = 0.3;

// All timing configuration settings are percentages and relative to the length of the note/rest being played.
// For instance, a quarter note with a correct start onset allowance of 0.15 will be considered in the green zone
// as long as the MIDI onset event is received within 15% of the quarter note's duration at the quarter note's expected onset.
export class TimingOffsetsConfig {
  // Left margin percentage around a note's onset for the correct or green zone.
  correctNoteOnsetStartPct = onsetDefault * .75
  // Right margin percentage around a note's onset for the correct or green zone.
  correctNoteOnsetEndPct = .2
  // Left margin percentage around a note's onset for the mistimed or yellow zone.
  mistimedNoteOnsetStartPct = .35
  // Right margin percentage around a note's onset for the mistimed or yellow zone.
  mistimedNoteOnsetEndPct = 0.6

  // Left margin percentage around a note's offset for the correct or green zone.
  correctNoteOffsetStartPct = offsetDefault
  // Right margin percentage around a note's offset for the correct or green zone.
  correctNoteOffsetEndPct = offsetDefault
  // Left margin percentage around a note's offset for the mistimed or yellow zone.
  mistimedNoteOffsetStartPct = 0.35
  // Right margin percentage around a note's offset for the mistimed or yellow zone.
  mistimedNoteOffsetEndPct = 0.25

  // The margin percentaes of rests SHRINK their bucket rather than growing it.

  // Left margin correct percentage around the start of the rest.
  // The correct zone shrinks "inward" (past the rest's onset) by this percentage.
  correctRestOnsetPct = 0.1
  // Left margin mistimed percentage around the start of the rest.
  // The mistimed zone shrinks "inward" (past the rest's onset) by this percentage.
  mistimedRestOnsetPct = 0.2
  // Right margin correcct percentage around the end of the rest.
  // The correct zone shrinks "inward" (before the rest's offset) by this percentage.
  correctRestOffsetPct = 0.25
  // Right margin mistimed percentage around the end of the rest.
  // The mistimed zone shrinks "inward" (before the rest's offset) by this percentage.
  mistimedRestOffsetPct = 0.25

}

// Timing offsets are relative to the expected timing for note on/off events.
export class TimingOffsets {
  // The max allowable timestamp ahead of the expected event time
  // for an event to be considered correctly timed in the correct or in the "green zone".
  correctStartTimestamp: number
  // The max allowable timestamp after the expected event time
  // for an event to be considered correctly timed in the correct or in the "green zone".
  correctEndTimestamp: number
  // The max allowable timestamp ahead of the expected event time
  // for an event to be considered mistimed or in the "yellow zone".
  mistimedStartTimestamp: number
  // The max allowable timestamp after the expected event time
  // for an event to be considered mistimed or in the "yellow zone".
  mistimedEndTimestamp: number

  constructor(correctStartTimestamp: number, correctEndTimestamp: number, mistimedStartTimestamp: number, mistimedEndTimestamp: number) {
    this.correctStartTimestamp = correctStartTimestamp
    this.correctEndTimestamp = correctEndTimestamp
    this.mistimedStartTimestamp = mistimedStartTimestamp
    this.mistimedEndTimestamp = mistimedEndTimestamp
  }
}



// Base class encapsulating common properties and methods for note and rest events.
export class BaseNoteEvent {
  protected length: number;
  protected timestamp: number;
  protected offsetTimestamp: number | null = null;
  private playedTimestamp: number | null = null;
  protected _hand: HANDEDNESS;
  protected snapshotPhraseAccuracy?: number = undefined;
  protected _LhSnapshotPhraseAccuracy?: number = undefined;
  protected _RhSnapshotPhraseAccuracy?: number = undefined;
  protected snapshotBPM?: number = undefined;
  protected errorScore: number = 0;
  protected eventBucket: EventBucket;
  private onsetState: MidiEventState = MidiEventState.UNSET;
  private offsetState: MidiEventState = MidiEventState.UNSET;

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    length: number,
    hand: HANDEDNESS
  ) {
    this.eventBucket = eventBucket;
    this.timestamp = timestamp;
    this.length = length;
    this._hand = hand;
  }

  public set OnsetState(eventState: MidiEventState) {
    this.onsetState = eventState;
  }

  public set OffsetState(eventState: MidiEventState) {
    this.offsetState = eventState;
  }
  public get Hand() {
    return this._hand;
  }

  public set SnapshotBPM(bpm: number) {
    this.snapshotBPM = bpm;
  }

  public set SnapshotPhraseAccuracy(accuracy: number) {
    this.snapshotPhraseAccuracy = accuracy;
  }

  public set RhSnapshotPhraseAccuracy(accuracy: number) {
    this._RhSnapshotPhraseAccuracy = accuracy;
  }

  public set LhSnapshotPhraseAccuracy(accuracy: number) {
    this._LhSnapshotPhraseAccuracy = accuracy;
  }

  public set PlayedTimestamp(playedTimestamp: number | null) {
    this.playedTimestamp = playedTimestamp;
  }

  public get OnsetState(): MidiEventState {
    return this.onsetState;
  }

  public get OffsetState(): MidiEventState {
    return this.offsetState;
  }

  public get SnapshotPhraseAccuracy() {
    return this.snapshotPhraseAccuracy || 0;
  }

  public get LhSnapshotPhraseAccuracy() {
    return this._LhSnapshotPhraseAccuracy || 0;
  }

  public get RhSnapshotPhraseAccuracy() {
    return this._RhSnapshotPhraseAccuracy || 0;
  }

  public get SnapshotBPM() {
    return this.snapshotBPM || 0;
  }

  public get Length() {
    return this.length;
  }

  public set Length(length: number) {
    this.length = length;
  }

  public get Timestamp() {
    return this.timestamp;
  }

  public set Timestamp(timestamp: number) {
    this.timestamp = timestamp;
  }

  public get OffsetTimestamp() {
    return this.offsetTimestamp;
  }

  public set OffsetTimestamp(offsetTimestamp: number | null) {
    this.offsetTimestamp = offsetTimestamp;
  }

  public set ErrorScore(errorScore: number) {
    this.errorScore = errorScore;
  }

  public get EventBucket() {
    return this.eventBucket;
  }

  public get PlayedTimestamp() {
    return this.playedTimestamp;
  }

  public get ErrorScore() {
    return this.errorScore;
  }

}

// Represents a single note event.
export class NoteEvent extends BaseNoteEvent {
  private midiNote: number;
  private noteOnTimingOffsets: TimingOffsets;
  private noteOffTimingOffsets: TimingOffsets;
  private graphics: PrerenderedGraphics[];
  private phraseRef?: Phrase;
  private additionalNoteRef: AdditionalNoteEvent | null = null;

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    midiNote: number,
    noteOnTimingOffsets: TimingOffsets,
    noteOffTimingOffsets: TimingOffsets,
    graphics: PrerenderedGraphics[],
    length: number,
    phraseRef: Phrase | undefined,
    hand: HANDEDNESS
  ) {
    super(eventBucket, timestamp, length, hand);
    this.midiNote = midiNote;
    this.noteOnTimingOffsets = noteOnTimingOffsets;
    this.noteOffTimingOffsets = noteOffTimingOffsets;
    this.graphics = graphics;
    this.phraseRef = phraseRef;
  }


  public get MidiNote() {
    return this.midiNote;
  }

  public set MidiNote(midiNote: number) {
    this.midiNote = midiNote;
  }

  public get NoteOnTimingOffsets() {
    return this.noteOnTimingOffsets;
  }

  public set NoteOnTimingOffsets(noteOnTimingOffsets: TimingOffsets) {
    this.noteOnTimingOffsets = noteOnTimingOffsets;
  }

  public get NoteOffTimingOffsets() {
    return this.noteOffTimingOffsets;
  }

  public set NoteOffTimingOffsets(noteOffTimingOffsets: TimingOffsets) {
    this.noteOffTimingOffsets = noteOffTimingOffsets;
  }

  public get Graphics() {
    return this.graphics;
  }

  public set Graphics(graphics: PrerenderedGraphics[]) {
    this.graphics = graphics;
  }

  public get PhraseRef() {
    return this.phraseRef;
  }

  public set PhraseRef(phraseRef: Phrase | undefined) {
    this.phraseRef = phraseRef;
  }

  public get AdditionalNote() {
    return this.additionalNoteRef;
  }

  public set AdditionalNote(note: AdditionalNoteEvent | null) {
    this.additionalNoteRef = note;
  }
}

// Represents a rest event.
export class RestEvent extends BaseNoteEvent {
  private timingOffsets: TimingOffsets;
  private state: MidiEventState = MidiEventState.UNSET;
  private graphics: PrerenderedGraphics[];
  private hidden: boolean;
  private additionalNoteRef: AdditionalNoteEvent | null = null;

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    timingOffsets: TimingOffsets,
    graphics: PrerenderedGraphics[],
    length: number,
    hidden: boolean,
    hand: HANDEDNESS
  ) {
    super(eventBucket, timestamp, length, hand);
    this.timingOffsets = timingOffsets;
    this.graphics = graphics;
    this.hidden = hidden;
  }

  public set AdditionalNote(note: AdditionalNoteEvent | null) {
    this.additionalNoteRef = note;
  }

  public get TimingOffsets() {
    return this.timingOffsets;
  }

  public get State() {
    return this.state;
  }

  public set State(state: MidiEventState) {
    this.state = state;
  }

  public get Graphics() {
    return this.graphics;
  }

  public get Hidden() {
    return this.hidden;
  }

  public get AdditionalNote(): AdditionalNoteEvent | null {
    return this.additionalNoteRef;
  }
}

// Represents an additional note event.
export class AdditionalNoteEvent extends BaseNoteEvent {
  private _playedNote: number;

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    playedTimestamp: number,
    playedNote: number,
    hand: HANDEDNESS
  ) {
    super(eventBucket, timestamp, 0, hand); // EventBucket is not applicable for AdditionalNoteEvent
    this._playedNote = playedNote;
    this.PlayedTimestamp = playedTimestamp;
  }

  public setOffsetTimestamp(offsetTimestamp: number) {
    this.offsetTimestamp = offsetTimestamp;
  }

  public get PlayedNote() {
    return this._playedNote;
  }
}

export class EventBucket {
  // Any audible notes that are played at this timestamp.
  notes: NoteEvent[]

  // We process rests differently from notes;
  // they get their own list to keep things cleaner.
  rests: RestEvent[]

  // The exact number of timestamp that events in this bucket onset at.
  // Per-note timing offsets are added and subtracted from this number.
  timestamp: number

  // The earliest timestamp that a MIDI event could match on a note/rest in this bucket.
  // Before this time has passed, there's no reason to look inside this bucket when matching MIDI note ON events.
  minOnsetTimestamp: number = -1

  // The latest timestamp that a MIDI event could match on a note/rest in this bucket.
  // After this time has passed, there's no reason to look inside this bucket when matching MIDI note ON events.
  maxOnsetTimestamp: number = -1

  // After this timestamp has passed and we aren't waiting for any more offset events,
  // the playback data from this bucket can be uploaded to the backend.
  maxOffsetTimestamp: number = -1

  // PhraseRef provides a link back to the phrase associated with the bucket
  phraseRef?: Phrase

  additinalNotes: AdditionalNoteEvent[] = []

  constructor(timestamp: number, notes: NoteEvent[], rests: RestEvent[], phraseRef: Phrase | undefined) {
    this.timestamp = timestamp
    this.notes = notes
    this.rests = rests
    this.phraseRef = phraseRef

    this.minOnsetTimestamp = 0
    this.maxOnsetTimestamp = 0
    this.maxOffsetTimestamp = 0
    for (let note of notes) {
      if (note.NoteOnTimingOffsets.mistimedStartTimestamp < this.minOnsetTimestamp) {
        this.minOnsetTimestamp = note.NoteOnTimingOffsets.mistimedStartTimestamp
      }
      // will probably need to revisit this
      if (note.NoteOnTimingOffsets.mistimedEndTimestamp > this.maxOnsetTimestamp) {
        this.maxOnsetTimestamp = note.NoteOnTimingOffsets.mistimedEndTimestamp
      }
      if (note.NoteOnTimingOffsets.mistimedEndTimestamp > this.maxOffsetTimestamp) {
        this.maxOffsetTimestamp = note.NoteOnTimingOffsets.mistimedEndTimestamp
      }
    }
  }

  addEvents(notes: NoteEvent[], rests: RestEvent[]) {
    this.notes.push(...notes)
    this.rests.push(...rests)
  }

  pushAdditionalNote(additionalNotes: AdditionalNoteEvent) {
    this.additinalNotes.push(additionalNotes)
  }

  sort() {
    this.notes.sort((n1, n2) => n1.NoteOnTimingOffsets.mistimedStartTimestamp - n2.NoteOnTimingOffsets.mistimedStartTimestamp);
    this.rests.sort((r1, r2) => r1.TimingOffsets.correctEndTimestamp - r2.TimingOffsets.correctEndTimestamp);

    this.minOnsetTimestamp = Math.min(
      ...this.notes.map(n => n.NoteOnTimingOffsets.mistimedStartTimestamp),
      ...this.rests.map(r => r.TimingOffsets.correctStartTimestamp)
    );
    this.maxOnsetTimestamp = Math.max(
      ...this.notes.map(n => n.NoteOnTimingOffsets.mistimedEndTimestamp),
      ...this.rests.map(r => r.TimingOffsets.mistimedEndTimestamp)
    );
    this.maxOffsetTimestamp = Math.max(
      ...this.notes.map(n => n.NoteOffTimingOffsets.mistimedEndTimestamp),
      ...this.rests.map(r => r.TimingOffsets.correctEndTimestamp)
    );
  }
}

export default class EventStream {
  timeKeeper: ITimeKeeper
  private offsetsConfig: TimingOffsetsConfig
  private eventBuckets: EventBucket[] = []
  private phraseHistory: Phrase[] = []
  private activeOnsetEvents: (NoteEvent | AdditionalNoteEvent | null)[]
  private lastProcessedTimestamp: number = -1
  private onsetIndex: number = 0
  private missedNotesIndex: number = 0
  private _lookbackWindowSize = 40;
  private _weightedAccuracyDivisions: number;
  private _weightedAccuracyHitVelocity: number;
  private _weightedAccuracyMissVelocity: number;
  private _realNotesMarkedCount: number = 0;

  private correctNoteMagnitude: number = 1
  private offNoteMagnitude: number = .8
  private missMagnitude: number = .3
  accuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[] = [];
  lhAccuracyQueue:(NoteEvent | RestEvent | AdditionalNoteEvent)[] = [];
  rhAccuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[] = [];
  id = uuid();
  private useRepertoireMarkAccuracy: boolean;
  public errorRecognitionActive: ErrorRecognitionActive = { left: true, right: true }
  // while this seems more UI related than midi-event processing related, the eventstream changes the color
  // of notes based on correctness, so must have access to that information, but doesn't have access to redux.
  private colorblindType: COLORBLIND_TYPE | null


  constructor(timeKeeper: ITimeKeeper, offsetsConfig: TimingOffsetsConfig, colorblindType: COLORBLIND_TYPE | null, weightedAccuracyDivisions: number = 1, weightedAccuracyHitVelocity: number = 0.025, weightedAccuracyMissVelocity: number = .1, useRepertoireMarkAccuracy: boolean = false) {
    this.timeKeeper = timeKeeper;
    this.offsetsConfig = offsetsConfig
    this._weightedAccuracyDivisions = weightedAccuracyDivisions;
    this._weightedAccuracyHitVelocity = weightedAccuracyHitVelocity;
    this._weightedAccuracyMissVelocity = weightedAccuracyMissVelocity;
    this.useRepertoireMarkAccuracy = useRepertoireMarkAccuracy;
    this.colorblindType = colorblindType
    if (this.useRepertoireMarkAccuracy) {
      this._lookbackWindowSize = 0;
    }

    this.activeOnsetEvents = []
    this.setupOnsetEvents()
    this.initAccuracyQueues()

  }

  get PhraseHistory() {
    return this.phraseHistory
  } 

  // workaround because the midi context loses reference to state. So, turn off error recognition here.
  setErrorRecognitionActive(errorRecognitionActive: ErrorRecognitionActive) {
    this.errorRecognitionActive = errorRecognitionActive;
  }


  initAccuracyQueues = () => {
    this.accuracyQueue = range(this._lookbackWindowSize).map(() => ({ErrorScore: 0} as NoteEvent))
    this.lhAccuracyQueue = range(this._lookbackWindowSize).map(() => ({ErrorScore: 0} as NoteEvent))
    this.rhAccuracyQueue = range(this._lookbackWindowSize).map(() => ({ErrorScore: 0} as NoteEvent))
  }

  accuracyQueueLength = () => this.accuracyQueue.length;

  totalNotesLength = () => {
    let totalNotesSum = 0;
    this.eventBuckets.forEach((el) => totalNotesSum += el.notes.length);
    return totalNotesSum
  };

  rollBackWindowFull = () => this.accuracyQueue.length >= this._lookbackWindowSize;

  // Returns the number of actual notes that have been marked for accuracy (not dummy initialized notes)
  hasRealNotesMarked = () => this._realNotesMarkedCount > 0;

  // Mark state. If current state is anything but unset, that value is removed from the queue and replaced. 
  private markAccuracy(accuracyQueue:(NoteEvent | RestEvent | AdditionalNoteEvent)[], noteEvent: NoteEvent | RestEvent | AdditionalNoteEvent) {
    // logger.info("pushing accuracy")
    if(noteEvent.OnsetState === MidiEventState.UNSET) {
      accuracyQueue.push(noteEvent)
    if (!this.useRepertoireMarkAccuracy) { // Repertoire does not use lookback window and doesn't need to cut array
      if (accuracyQueue.length > this._lookbackWindowSize) {
        accuracyQueue.shift()
      }
    }
    }
  }

  private serErrorScore(note: NoteEvent | RestEvent, eventState: MidiEventState) {
    if (
      eventState === MidiEventState.ADDITIONAL_NOTE &&
      note.AdditionalNote !== null &&
      note.ErrorScore == 0
    ) {
      note.ErrorScore = .7
    } else if (isSemiErrorState(eventState) && eventState !== MidiEventState.ADDITIONAL_NOTE && note.ErrorScore !== 1) {
      note.ErrorScore = .7
    } else if (isErrorState(eventState)) {
      note.ErrorScore = 1
    }
  }

  private markAccuracyBothHands(note: NoteEvent | RestEvent, eventState: MidiEventState) {
    // in the case of a hit or unset, if there is an additional note, we want to adjust the score to
    // adjust for the error
    this.serErrorScore(note, eventState)
    this.markAccuracy(this.accuracyQueue, note)
    // Track that a real note has been marked for accuracy (not a dummy initialized note)
    this._realNotesMarkedCount++;
    if (note.Hand === 'lh') {
      this.markAccuracy(this.lhAccuracyQueue, note)
    } else if (note.Hand === 'rh') {
      this.markAccuracy(this.rhAccuracyQueue, note)
    }

  }

  private makeNoteColor(note: NoteEvent | RestEvent, eventState: MidiEventState) {
    this.serErrorScore(note, eventState)
    if (eventState === MidiEventState.HIT) {
      this.makeNoteCorrect(note.Graphics, this.colorblindType, note.Timestamp)
    } else if (midiSemiErrorStates.contains(eventState)) {
      this.makeNoteSemiError(note.Graphics, this.colorblindType, note.Timestamp)
    } else if (midiFullErrorStates.contains(eventState)) {
      this.makeNoteError(note.Graphics, this.colorblindType, note.Timestamp)
    }
  }


  // markOffsetAccuracy does not call markAccuracy
  private setOffsetAccuracy(note: NoteEvent, eventState: MidiEventState) {
    this.makeNoteColor(note, eventState);
    note.OffsetState = eventState;
    note.SnapshotPhraseAccuracy = this.calcAccuracy();
    note.SnapshotBPM = this.timeKeeper.getBpm();

  }

  protected markOnsetAccuracyWithSideEffects(note: NoteEvent, eventState: MidiEventState, timestamp: number, pulse: boolean = false, midiNote: number | null) {
    note.PlayedTimestamp = timestamp
    if (eventState !== MidiEventState.ADDITIONAL_NOTE && midiNote) {
      this.activeOnsetEvents[midiNote] = note;
    }
    // only pulse on correct. At least that's how the code reads currently.
    if (eventState === MidiEventState.HIT && pulse) {
      this.pulseMatchedNote(note, ERROR_REC_CLASS.CORRECT, note.Timestamp)
    }
    this.markAccuracyBothHands(note, eventState)
    // no need to set state for additional note, because additional note is a separate event
    if (eventState !== MidiEventState.ADDITIONAL_NOTE) {
      note.OnsetState = eventState;
    }
    this.makeNoteColor(note, eventState);
    if ((eventState === MidiEventState.ADDITIONAL_NOTE || eventState === MidiEventState.WRONG_NOTE) && midiNote && note.AdditionalNote === null) {
      const additionalNoteEvent = new AdditionalNoteEvent(note.EventBucket, note.Timestamp, timestamp, midiNote, note.Hand)
      note.AdditionalNote = additionalNoteEvent;
    }
    if (eventState === MidiEventState.ADDITIONAL_NOTE && midiNote) {
      const additionalNoteEvent = new AdditionalNoteEvent(note.EventBucket, note.Timestamp, timestamp, midiNote, note.Hand)
      note.EventBucket.pushAdditionalNote(additionalNoteEvent)
      this.activeOnsetEvents[midiNote] = additionalNoteEvent;
    }
  
  }

  protected markOffsetAccuracyWithSideEffects(note: NoteEvent, eventState: MidiEventState, timestamp: number | null) {
    this.makeNoteColor(note, eventState)
    note.OffsetState = eventState
    note.LhSnapshotPhraseAccuracy = this.calcLHAccuracy()
    note.RhSnapshotPhraseAccuracy = this.calcRHAccuracy()
    note.SnapshotPhraseAccuracy = this.calcAccuracy()
    note.OffsetTimestamp = timestamp
  }

  private markRestAccuracyWithSideEffects(rest: RestEvent, additionalNote: number, timestamp: number) {
    this.markAccuracyBothHands(rest, MidiEventState.ADDITIONAL_NOTE)
    makeNoteSemiError(rest.Graphics, this.colorblindType);
    const additionalNoteEvent = new AdditionalNoteEvent(rest.EventBucket, rest.Timestamp, timestamp, additionalNote, rest.Hand)
    rest.AdditionalNote = additionalNoteEvent;
    rest.EventBucket.pushAdditionalNote(additionalNoteEvent)
    this.activeOnsetEvents[additionalNote] = additionalNoteEvent;
  }


  calcAccuracyHelper(accuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[]) {
    const accuracyReversed = accuracyQueue.slice().reverse()
    const accuracyLength = accuracyReversed.length;
    const numerator = sum(accuracyReversed.map((evt, indx) => {
      let sumTarget
      if (evt.ErrorScore === 0) {
        // sumTarget = (accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx) >= 0 ? ((accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx)) : 0
        return (accuracyLength - indx)
      } else if (evt.ErrorScore  < 1) {
        // start subtracting from top of magnitude... i.e. 40 * .5 is 20 so 20, 19, 18...
        // and when we get to zero 
        sumTarget = (accuracyLength - indx) * this.correctNoteMagnitude >= 0 ? (accuracyLength - indx) * this.correctNoteMagnitude : 0
        return sumTarget * this.offNoteMagnitude
      } else {
        return 0
      }
    }))
    const denominator = sum(accuracyReversed.map((evt, indx) => {
      let sumTarget;

      if (evt.ErrorScore === 0) {
        // sumTarget = (accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx) >= 0 ? ((accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx)) : 0
        return (accuracyLength - indx)
      } else if (evt.ErrorScore  < 1) {
        // start subtracting from top of magnitude... i.e. 40 * .5 is 20 so 20, 19, 18...
        // and when we get to zero 
        sumTarget = (accuracyLength - indx) * this.correctNoteMagnitude >= 0 ? (accuracyLength - indx) * this.correctNoteMagnitude : 0
        return sumTarget
      } else {
        // same same but different
        sumTarget = (accuracyLength - indx) * this.missMagnitude >= 0 ? (accuracyLength - indx) * this.missMagnitude : 0
        return sumTarget
      }
    }))
    // logger.info("returning accuracy " + (numerator / denominator) * 100)
    return (numerator / denominator) * 100
  }



  calcAccuracy() {
    // logger.info("accuracy queue: ", this.accuracyQueue)
    const acc = this.calcAccuracyHelper(this.accuracyQueue);
    // logger.info("accuracy=", acc)
    // logger.info(this.accuracyQueue)
    return acc;
  }

  calcLHAccuracy() {
    // logger.info("lh accuracy queue: ", this.lhAccuracyQueue)
    const lhAccuracy = this.calcAccuracyHelper(this.lhAccuracyQueue);
    // logger.info("lh accuracy", lhAccuracy)
    return lhAccuracy
  }

  calcRHAccuracy() {
    // logger.info("rh accuracy queue ", this.rhAccuracyQueue)
    const rhAccuracy = this.calcAccuracyHelper(this.rhAccuracyQueue);
    // logger.info("rh accuracy", rhAccuracy)
    return rhAccuracy;
  }


  calcRepAccuracy(tempo: number, performanceTempo: number): [number, number[]] {
    // I had to bring this logic of "setAccuracyAtTempo" inside the midi stream, because in the context
    // "midiStream.accuracyQueue" does not update unless some other action causes re-render
    //  (you can observe by logging "midiStream.accuracyQueue" in the RepertoirePlayContext vs logging "this.accuracyQueue" here in EventStream)
    const accuracyLength = this.accuracyQueue.length;
    const accuracyReversed = this.accuracyQueue.slice().reverse()
    const numerator = accuracyLength - sum(accuracyReversed.map((evt, indx) => evt.ErrorScore))
    const denominator = this.accuracyQueue.filter(note => note.OnsetState !== MidiEventState.ADDITIONAL_NOTE).length
    let accuracyAtTempo = this.accuracyQueue.map((v) => {
      let performanceFac = Math.min(tempo / performanceTempo, 1)
      if(v.ErrorScore == 1) {
        return 0
      } else if(v.ErrorScore == 0) {
        return performanceFac
      } else {
        return v.ErrorScore * performanceFac
      }
    })

    return [(numerator / denominator) * 100, accuracyAtTempo]
  }

  private setupOnsetEvents() {
    // There are 128 MIDI event pitches.
    // Allocate a fixed size array with 128 slots,
    // and incoming MIDI on/off events will be "mapped" using their pitch into this array.
    this.activeOnsetEvents = new Array(128)
    this.activeOnsetEvents.fill(null)
    // See: https://stackoverflow.com/a/44853951
    Object.seal(this.activeOnsetEvents)

    let str = ""
    for (let i = 0; i <= 24; i++) {
      const x = (1 / 24) * i
      str = str + `\n${4 * x}, ${4 * this.calculateCorrectNoteOffMin(x)}`
    }
  }

  reset() {
    logger.info("resetting event stream")
    this.eventBuckets = []
    this.lastProcessedTimestamp = -1
    this.onsetIndex = 0
    this.missedNotesIndex = 0
    this._realNotesMarkedCount = 0
    this.setupOnsetEvents()
    this.initAccuracyQueues()
    // this.accuracyQueue = []
  }

  addEvents2(iterator: Iterator | IteratorPhaser, startMeasure: number) {
    iterator.resetIterator()

    while (!iterator.EndReached) {

      const notes: NoteEvent[] = []
      const rests: RestEvent[] = []
      const onsetTimestamp = startMeasure + iterator.currentTimeStamp
      // Check if there's already a bucket for this timestamp.
      // OSMD's iterator WILL repeat timestamps.
      let bucket = this.eventBuckets.find(b => Math.abs(b.timestamp - onsetTimestamp) < Number.EPSILON)
      if (bucket === undefined) {
        bucket = new EventBucket(onsetTimestamp, [], [], iterator.phrase)
        iterator?.phrase?.pushPhrasePlayData(bucket)
        this.eventBuckets.push(bucket)
      }

      iterator.CurrentVoiceEntries.forEach(currentVoiceEntry => {
        for (const note of currentVoiceEntry.Notes) {
          // console.log("adding note", note)
          // todo: figure out how to do note-tie in prerendering 
          const noteDuration = note.length
          // calculateTiedNoteLength() will return a negative value 
          // if a tied note should be discarded for event stream purposes.
          // This is now performed in the prerenderer
          if (noteDuration <= 0) {
            return
          }
          const offsetTimestamp = onsetTimestamp + noteDuration
          const correctNoteOffsetStartTimestamp = onsetTimestamp + this.calculateCorrectNoteOffMin(noteDuration)

          const startAllowance = 0.25
          const endAllowance = 0.25

          if (note.isRest) {
            const restEvent = new RestEvent(
              bucket as EventBucket,
              onsetTimestamp,
              new TimingOffsets(
                onsetTimestamp === 0 ? -.2 : onsetTimestamp - startAllowance * this.offsetsConfig.correctRestOnsetPct,
                offsetTimestamp - endAllowance * this.offsetsConfig.correctRestOffsetPct,
                onsetTimestamp === 0 ? -.2 : onsetTimestamp - startAllowance * this.offsetsConfig.mistimedRestOnsetPct,
                offsetTimestamp - endAllowance * this.offsetsConfig.mistimedRestOffsetPct
              ),
              note.graphics,
              note.length,
              !note.printObject,
              note.staffPosition === 'bottom' ? HANDEDNESS.LEFT : HANDEDNESS.RIGHT
            )
            rests.push(restEvent)
          } else {
            const noteOnTimestamps = new TimingOffsets(
              onsetTimestamp === 0 ? -.2 : onsetTimestamp - startAllowance * this.offsetsConfig.correctNoteOnsetStartPct,
              onsetTimestamp + endAllowance * this.offsetsConfig.correctNoteOnsetEndPct,
              onsetTimestamp === 0 ? -.2 : onsetTimestamp - startAllowance * this.offsetsConfig.mistimedNoteOnsetStartPct,
              onsetTimestamp + endAllowance * this.offsetsConfig.mistimedNoteOnsetEndPct
            )

            const noteOffTimestamps = new TimingOffsets(
              correctNoteOffsetStartTimestamp,
              offsetTimestamp + endAllowance * this.offsetsConfig.correctNoteOffsetEndPct,
              correctNoteOffsetStartTimestamp - noteDuration * 0.2,
              offsetTimestamp + endAllowance * this.offsetsConfig.mistimedNoteOffsetEndPct
            )

            const graphics: PrerenderedGraphics[] = note.noteTie
              ? note?.noteTie?.Notes?.map((n: any) => n.graphics)
              : note.graphics
            if (note.staffPosition === 'unknown') {
              console.warn("unknown staff position on note")
            }
            const noteEvent = new NoteEvent(
              bucket as EventBucket,
              onsetTimestamp,
              note.pitch.halfTone + 12,
              noteOnTimestamps,
              noteOffTimestamps,
              graphics,
              note.length,
              iterator.phrase,
              note.staffPosition === 'bottom' ? HANDEDNESS.LEFT : HANDEDNESS.RIGHT
            )
            // console.log("adding note event", noteEvent) 
            notes.push(noteEvent)

          }
        }
      });
      bucket.addEvents(notes, rests)
      iterator.next()
    }

    // Tell all the event buckets to sort their internals and then we'll sort the buckets themselves.
    this.eventBuckets.forEach(b => b.sort())
    this.eventBuckets.sort((b1, b2) => b1.minOnsetTimestamp - b2.minOnsetTimestamp)
    
    // Debug logging for event addition
    console.log("[EventStream.addEvents2] Added events for phrase at startMeasure:", startMeasure, "totalBuckets:", this.eventBuckets.length, "currentOnsetIndex:", this.onsetIndex);
    if (this.eventBuckets.length > 0) {
      const firstBucket = this.eventBuckets[0];
      const lastBucket = this.eventBuckets[this.eventBuckets.length - 1];
      console.log("[EventStream.addEvents2] Bucket range - first:", firstBucket.timestamp, "last:", lastBucket.timestamp);
    }
    
    if (iterator.phrase) {
      this.phraseHistory.push(iterator.phrase);
    }
  }

  // this function returns the last phrase N before the current phrase - starting with 1 being the phrase
  // directly before the current phrase
  getNBeforePhrase = (nBefore: number): Phrase | undefined => {
    let currentIndex = this.onsetIndex;
    let nPhrases = 0;
    let currentPhrase = this.eventBuckets[currentIndex]?.phraseRef
    if (currentPhrase) {
      while (nPhrases < nBefore && currentPhrase) {

        if (!(this.eventBuckets[currentIndex]?.phraseRef?.uuid == currentPhrase.uuid)) {
          nPhrases++;
        }
        currentIndex--;
      }
      return this.eventBuckets[currentIndex]?.phraseRef
    } else {
      return this.eventBuckets?.last()?.phraseRef
    }

  }


  // God speed to any who dare edit below this line. 
  handleNoteOnEvent(midiNote: number, velocity: number) {
    logger.info("note on in midi stream: ", midiNote)
    if (!this.errorRecognitionActive) { return; }
    const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
    if (this.timeKeeper.isPaused()) {
      return;
    }
    let closestNoteMatch: NoteEvent | null = null;
    let closestPitchDiff: number = -1;
    let restMatches: RestEvent[] = [];
    let possibleNotes: NoteEvent[] = [];
    let exactMatchFound = false;
    let possibleNoteBuckets: EventBucket[] = [];
    let earlyOnset = false;
    let lateOnset = false;
    let latestOnsetIndex = 0;
    
    // Debug logging for note registration issues
    console.log("[EventStream] handleNoteOnEvent - timestamp:", timestamp, "onsetIndex:", this.onsetIndex, "totalBuckets:", this.eventBuckets.length);
    
    // Log bucket boundaries around current onsetIndex
    if (this.onsetIndex > 0 && this.eventBuckets[this.onsetIndex - 1]) {
      const prevBucket = this.eventBuckets[this.onsetIndex - 1];
      console.log("[EventStream] prevBucket:", this.onsetIndex - 1, "minOnset:", prevBucket.minOnsetTimestamp, "maxOffset:", prevBucket.maxOffsetTimestamp);
    }
    if (this.eventBuckets[this.onsetIndex]) {
      const currBucket = this.eventBuckets[this.onsetIndex];
      console.log("[EventStream] currBucket:", this.onsetIndex, "minOnset:", currBucket.minOnsetTimestamp, "maxOffset:", currBucket.maxOffsetTimestamp);
    }
    if (this.eventBuckets[this.onsetIndex + 1]) {
      const nextBucket = this.eventBuckets[this.onsetIndex + 1];
      console.log("[EventStream] nextBucket:", this.onsetIndex + 1, "minOnset:", nextBucket.minOnsetTimestamp, "maxOffset:", nextBucket.maxOffsetTimestamp);
    }

    for (let i = (this.onsetIndex > 0 ? this.onsetIndex - 1 : this.onsetIndex); i < this.eventBuckets.length; i++) {
      let bucket = this.eventBuckets[i];
      
      const inBucket = this.isInBucket(timestamp, i);
      if (!inBucket) {
        // Log why it wasn't in bucket for first few iterations
        if (i < this.onsetIndex + 3) {
          console.log("[EventStream] Bucket", i, "NOT in bucket - timestamp:", timestamp, "min:", bucket.minOnsetTimestamp, "max:", bucket.maxOffsetTimestamp);
        }
        continue;
      } else if (timestamp < bucket.minOnsetTimestamp) {
        console.log("[EventStream] Breaking at bucket", i, "- timestamp < minOnset");
        break;
      }
      
      console.log("[EventStream] Found matching bucket", i, "at timestamp", bucket.timestamp);

      latestOnsetIndex = i;
      possibleNotes = possibleNotes.concat(bucket.notes);
      possibleNoteBuckets = possibleNoteBuckets.concat(bucket);
      restMatches = restMatches.concat(bucket.rests);

      for (const noteEvent of bucket.notes) {
        if (timestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp
          || timestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp) {
            console.log("timestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp", timestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp)
            console.log("timestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp", timestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp)
            console.log("skipping noteEvent", noteEvent)
          continue;
        }

        const pitchDiff = Math.abs(noteEvent.MidiNote - midiNote);
        console.log("pitchDiff", pitchDiff)
        console.log("noteEvent.MidiNote", noteEvent.MidiNote)
        console.log("midiNote", midiNote)
        if (pitchDiff === 0) {
          closestNoteMatch = noteEvent;
          exactMatchFound = true;
          closestPitchDiff = 0;
          console.log("timestamp < noteEvent.NoteOnTimingOffsets.correctStartTimestamp", timestamp < noteEvent.NoteOnTimingOffsets.correctStartTimestamp)
          if (timestamp < noteEvent.NoteOnTimingOffsets.correctStartTimestamp) {
            earlyOnset = true;
          }
          console.log("timestamp > noteEvent.NoteOnTimingOffsets.correctEndTimestamp", timestamp > noteEvent.NoteOnTimingOffsets.correctEndTimestamp)
          if (timestamp > noteEvent.NoteOnTimingOffsets.correctEndTimestamp) {
            lateOnset = true;
          }
        } else if (closestNoteMatch == null) {
          closestNoteMatch = noteEvent;
          closestPitchDiff = pitchDiff;
        } else {
          const closestMatchTimestampDiff = Math.abs(closestNoteMatch.Timestamp - timestamp);
          const timestampDiff = Math.abs(noteEvent.Timestamp - timestamp);
          if (closestPitchDiff >= pitchDiff && closestMatchTimestampDiff >= timestampDiff) {
            closestNoteMatch = noteEvent;
            closestPitchDiff = pitchDiff;
          }
        }
      }
    }
    console.log("timestamp", timestamp)
    console.log("possibleNotes", possibleNotes)
    possibleNotes.sort((a, b) => b.Timestamp - a.Timestamp);
    possibleNoteBuckets.sort((eventBucket1, eventBucket2) => Math.abs(eventBucket1.timestamp - timestamp) - Math.abs(eventBucket2.timestamp - timestamp));

    const isAttemptedNoteInChord = (possibleNoteBuckets.length > 0 && (possibleNoteBuckets[0].notes.length > 1));
    if (latestOnsetIndex >= this.onsetIndex || isAttemptedNoteInChord) {
      console.log("latestOnsetIndex", latestOnsetIndex)
      console.log("this.onsetIndex", this.onsetIndex)
      console.log("isAttemptedNoteInChord", isAttemptedNoteInChord)
      console.log("exactMatchFound", exactMatchFound)
      console.log("closestNoteMatch", closestNoteMatch)
      if (closestNoteMatch && exactMatchFound && closestNoteMatch.OffsetState === MidiEventState.UNSET) {
        const closestNoteBucket = possibleNoteBuckets[0];
        if (!this.checkAccuracyNoteHand(closestNoteMatch)) return;

        const isChord = closestNoteBucket.notes.length > 1;

        if (closestNoteMatch.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp
          && closestNoteMatch.NoteOnTimingOffsets.mistimedEndTimestamp >= timestamp
          && closestNoteMatch.OffsetState === MidiEventState.UNSET) {
          console.log("closestNoteMatch", closestNoteMatch)
          if (closestNoteMatch.OnsetState === MidiEventState.UNSET) {

            if (!(earlyOnset || lateOnset)) {
              console.log("hit 1")
              this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.HIT, timestamp, true, midiNote);
            } else if (earlyOnset) {
              this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.EARLY_ONSET, timestamp, true, midiNote);
            } else if (lateOnset) {
              this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.LATE_ONSET, timestamp, true, midiNote);
            }
          } else if (!isErrorState(closestNoteMatch.OnsetState) || (isErrorState(closestNoteMatch.OnsetState) && isChord)) {
            this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.ADDITIONAL_NOTE, timestamp, false, midiNote);
          }
        }
      } else if ((closestNoteMatch != null && possibleNotes.length == 1 && !(possibleNoteBuckets[0].rests.length > 0 && possibleNoteBuckets[0].notes.length === 0)) ||
        (closestNoteMatch == null && possibleNotes.length == 1 && !(possibleNoteBuckets[0].rests.length > 0 && possibleNoteBuckets[0].notes.length === 0))) {
        if (closestNoteMatch === null) {
          closestNoteMatch = possibleNotes[0];
        }
        if (!this.checkAccuracyNoteHand(closestNoteMatch)) return;

        if (closestNoteMatch.OffsetState === MidiEventState.UNSET) {
          if (closestNoteMatch.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp
            && closestNoteMatch.NoteOnTimingOffsets.mistimedEndTimestamp >= timestamp) {
              console.log("closestNoteMatch", closestNoteMatch)
            if (closestNoteMatch.OnsetState === MidiEventState.UNSET) {
              this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.WRONG_NOTE, timestamp, false, midiNote);
            } else if (closestNoteMatch.OnsetState === MidiEventState.HIT) {
              console.log("hit 2")
              this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.ADDITIONAL_NOTE, timestamp, false, midiNote);
            } else {
              this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.ADDITIONAL_NOTE, timestamp, false, midiNote);
            }
          } else {
            this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.ADDITIONAL_NOTE, timestamp, false, midiNote);
          }
        } else {
          this.markOnsetAccuracyWithSideEffects(closestNoteMatch, MidiEventState.ADDITIONAL_NOTE, timestamp, false, midiNote);
        }
      } else if (possibleNotes.length > 1) {
        possibleNotes.sort((note1, note2) => Math.abs(midiNote - note1.MidiNote) - Math.abs(midiNote - note2.MidiNote));
        let closestInBucket: NoteEvent | undefined = undefined;

        const allPlayed = every(possibleNotes, note => note.OnsetState !== MidiEventState.UNSET);
        if (allPlayed) {
          for (let i = 0; i < possibleNotes.length; i++) {
            const currNote = possibleNotes[i];
            const inTimespan = currNote.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp
              && currNote.NoteOffTimingOffsets.correctStartTimestamp >= timestamp;
            exactMatchFound = currNote.MidiNote === midiNote;
            if (inTimespan &&
              (exactMatchFound ||
                (!isErrorState(currNote.OnsetState) && currNote.OffsetState === MidiEventState.UNSET))) {
              closestInBucket = currNote;
              break;
            }
          }
          if (closestInBucket) {
            this.markOnsetAccuracyWithSideEffects(closestInBucket, MidiEventState.ADDITIONAL_NOTE, timestamp, false, midiNote);
            closestNoteMatch = closestInBucket;
          }
        } else {
          for (let i = 0; i < possibleNotes.length; i++) {
            const currNote = possibleNotes[i];
            const inTimespan = currNote.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp
              && currNote.NoteOffTimingOffsets.correctStartTimestamp >= timestamp;
            exactMatchFound = currNote.MidiNote === midiNote;
            if (inTimespan &&
              (exactMatchFound ||
                (currNote.OnsetState === MidiEventState.UNSET && currNote.OffsetState === MidiEventState.UNSET))) {
              closestInBucket = currNote;
              closestNoteMatch = closestInBucket;
              break;
            }
          }
          if (closestInBucket && !this.checkAccuracyNoteHand(closestInBucket)) return;

          if (closestInBucket && exactMatchFound && closestInBucket.OffsetState === MidiEventState.UNSET) {
            if (earlyOnset) {
              this.markOnsetAccuracyWithSideEffects(closestInBucket, MidiEventState.EARLY_ONSET, timestamp, false, midiNote);
            } else if (lateOnset) {
              this.markOnsetAccuracyWithSideEffects(closestInBucket, MidiEventState.LATE_ONSET, timestamp, false, midiNote);
            } else {
              console.error("exact match fell through but was not early or late");
              this.markOnsetAccuracyWithSideEffects(closestInBucket, MidiEventState.HIT, timestamp, true, midiNote);
            }
          } else if (closestInBucket && !isErrorState(closestInBucket.OnsetState) && closestInBucket.OffsetState === MidiEventState.UNSET) {
            this.markOnsetAccuracyWithSideEffects(closestInBucket, MidiEventState.WRONG_NOTE, timestamp, false, midiNote);
          }
        }
      } else {
        restMatches.sort((rest1, rest2) => Math.abs(midiNote - rest1.Timestamp) - Math.abs(midiNote - rest2.Timestamp));

        let closestRestMatch: RestEvent | undefined = restMatches.length > 0 ? restMatches[0] : undefined;
        for (let rest of restMatches) {
          if (rest.State === MidiEventState.UNSET &&
            !rest.Hidden &&
            (!closestRestMatch ||
              (closestRestMatch &&
                (Math.abs(timestamp - rest.Timestamp) < Math.abs(timestamp - closestRestMatch.Timestamp))))) {
            closestRestMatch = rest;
          }
        }
        if (closestRestMatch && !closestRestMatch.Hidden) {
          this.markRestAccuracyWithSideEffects(closestRestMatch, midiNote, timestamp);
          if (closestRestMatch?.Hand === HANDEDNESS.LEFT) {
            closestRestMatch.LhSnapshotPhraseAccuracy = this.calcLHAccuracy();
          }
          if (closestRestMatch?.Hand === HANDEDNESS.RIGHT) {
            closestRestMatch.RhSnapshotPhraseAccuracy = this.calcRHAccuracy();
          }
          closestRestMatch.SnapshotPhraseAccuracy = this.calcAccuracy();
          closestRestMatch.SnapshotBPM = this.timeKeeper.getBpm();
          closestRestMatch.OffsetTimestamp = timestamp;
        }
      }
    }
    const currentPhrase = this.getNBeforePhrase(0);
    if (currentPhrase) {
      currentPhrase.pushEvent({ midiEvent: MidiEventType.NOTE_ON, midiNote, timestamp: timestamp - currentPhrase.getStartTimestamp() });
      currentPhrase.pushRHRunningAccuracy(this.calcAccuracy());
      if (closestNoteMatch) {
        closestNoteMatch.SnapshotPhraseAccuracy = this.calcAccuracy();
        if (closestNoteMatch.Hand === HANDEDNESS.LEFT) {
          closestNoteMatch.LhSnapshotPhraseAccuracy = this.calcLHAccuracy();
        }
        if (closestNoteMatch.Hand === HANDEDNESS.RIGHT) {
          closestNoteMatch.RhSnapshotPhraseAccuracy = this.calcRHAccuracy();
        }
      }
      currentPhrase.getHasLeftHandedNotes() && currentPhrase.pushLHRunningAccuracy(this.calcLHAccuracy());
      currentPhrase.getHasRightHandedNotes() && currentPhrase.pushRHRunningAccuracy(this.calcRHAccuracy());
    }
  }


  handleNoteOffEvent(midiNote: number) {
    const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
    // if (this.timeKeeper.isPaused()) {
    //   return;
    // }
    const currentPhrase = this.getNBeforePhrase(0);
    if (currentPhrase) {
      // might not be necessary. I think we're going to go with the event stream over raw midi. It contains the midi info. 
      // This may be deleted in the future. Not sure yet.
      currentPhrase.pushEvent({ midiEvent: MidiEventType.NOTE_OFF, midiNote, timestamp: timestamp - currentPhrase.getStartTimestamp() });
    }

    if (!this.errorRecognitionActive.left && !this.errorRecognitionActive.right) {
      logger.debug("error rec turned off - returning from note off");
      return;
    }
    // Offset events are much easier to handle than onset events!
    // We already matched the onset event earlier and kept track of it. 
    // All we have to do now is re-color the note if an offset event was out of range.
    const match = this.activeOnsetEvents[midiNote];
    if(!match) {
      return;
    }
    match.OffsetTimestamp = timestamp;
    match.SnapshotBPM = this.timeKeeper.getBpm();
    if (match?.Hand === HANDEDNESS.LEFT && !this.errorRecognitionActive.left) {
      logger.debug("left hand error rec turned off - returning from note off");
      return;
    }
    if (match?.Hand === HANDEDNESS.RIGHT && !this.errorRecognitionActive.right) {
      logger.debug("right hand error rec turned off - returning from note off");
      return;
    }
    if (match instanceof AdditionalNoteEvent) {
      return;
    }

    if (match != null && match.OffsetState === MidiEventState.UNSET) {
      // make sure to set bpm on notes that were missed
      match.SnapshotBPM = this.timeKeeper.getBpm();
      const timingOffsets = match.NoteOffTimingOffsets;

      if (match.OnsetState === MidiEventState.WRONG_NOTE) {
        this.setOffsetAccuracy(match, MidiEventState.WRONG_NOTE); // if onset is wrong note, offset is as well
      } else if (
        timestamp >= timingOffsets.correctStartTimestamp &&
        timestamp <= timingOffsets.correctEndTimestamp &&
        match.OnsetState === MidiEventState.HIT &&
        // additional note has been moved to its own event, so check if the additional note is null
        match.AdditionalNote === null
      ) {
        // WOOHOO, they hit the correct zone
        if (!isSemiErrorState(match.OnsetState)) {
          this.markOffsetAccuracyWithSideEffects(match, MidiEventState.HIT, timestamp);
        }
      } else if (timestamp <= timingOffsets.mistimedStartTimestamp) {
        this.markOffsetAccuracyWithSideEffects(match, MidiEventState.EARLY_OFFSET, timestamp);
      } else if (timestamp >= timingOffsets.correctEndTimestamp) {
        this.markOffsetAccuracyWithSideEffects(match, MidiEventState.LATE_OFFSET, timestamp);
      }

      // Now that we've processed the note off event, we're no longer actively tracking this note,
      // and can unset it in the events array.
      this.activeOnsetEvents[midiNote] = null;
    }
  }

  checkAccuracyNoteHand(note: NoteEvent | null) {
    if (note != null) {
      if (note.Hand === HANDEDNESS.RIGHT && !this.errorRecognitionActive.right) return false
      if (note.Hand === HANDEDNESS.LEFT && !this.errorRecognitionActive.left) return false
    }
    return true
  }

  // Should be called on a set interval. Looks for missed notes and colors them yellow/red.
  update() {
    if (!this.errorRecognitionActive.left && !this.errorRecognitionActive.right) {
      return;
    }
    // Don't update if we re-wound and are counting back in.
    const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
    if (timestamp < this.lastProcessedTimestamp || this.missedNotesIndex >= this.eventBuckets.length) {
      return;
    }

    this.lastProcessedTimestamp = timestamp;

    // Look for notes in buckets that should be colored yellow or red.
    for (let i = this.onsetIndex; i < this.eventBuckets.length && this.isInBucket(timestamp, i); i++) {
      const bucket = this.eventBuckets[i];
      bucket.notes.forEach(note => {
        if (!this.checkAccuracyNoteHand(note)) return;

        // If a note on event has been received for this note, ignore it.
        if (note.OnsetState != MidiEventState.UNSET) {
          return;
        }

        if (timestamp > note.NoteOnTimingOffsets.mistimedEndTimestamp) {
          // Mistimed window was missed, color error
          this.markOnsetAccuracyWithSideEffects(note, MidiEventState.NEVER_PLAYED, -1, false, null);

          note.OffsetState = MidiEventState.NEVER_PLAYED;
        } else if (timestamp > note.NoteOnTimingOffsets.correctEndTimestamp) {
          // Correct window was missed, color mistimed
        }
      });
    }

    // Look for held-down notes whose offset buckets were missed.
    this.activeOnsetEvents.forEach(note => {
      if (note instanceof AdditionalNoteEvent) {
        return;
      }
      if (!this.checkAccuracyNoteHand(note)) return;

      if (note && note.OffsetState == MidiEventState.UNSET
        && note.NoteOffTimingOffsets.mistimedEndTimestamp < timestamp) {
        if (note.OnsetState === MidiEventState.UNSET) {
          this.markOffsetAccuracyWithSideEffects(note, MidiEventState.NEVER_PLAYED, null);
        } else if (note.OnsetState === MidiEventState.HIT) {
          this.markOffsetAccuracyWithSideEffects(note, MidiEventState.LATE_OFFSET, null);
        }
      }
    });

    // Advance the onset index if we've exceeded the max onset for this bucket.
    while (this.eventBuckets[this.onsetIndex] && timestamp > this.eventBuckets[this.onsetIndex].maxOnsetTimestamp) {
      const bucket = this.eventBuckets[this.onsetIndex];
      console.log("[EventStream.update] Advancing onsetIndex from", this.onsetIndex, "- timestamp:", timestamp, "maxOnset:", bucket.maxOnsetTimestamp, "bucketTimestamp:", bucket.timestamp);
      bucket.notes.forEach(note => {
        if (!this.checkAccuracyNoteHand(note)) return;

        if (note.OnsetState === MidiEventState.UNSET) {
          console.log("[EventStream.update] Marking note as NEVER_PLAYED - midiNote:", note.MidiNote, "noteTimestamp:", note.Timestamp);
          this.markOnsetAccuracyWithSideEffects(note, MidiEventState.NEVER_PLAYED, -1, false, null);
          this.setOffsetAccuracy(note, MidiEventState.NEVER_PLAYED);
        }
      });
      this.onsetIndex++;
    }
  }

  private calculateTiedNoteLength(note: Note): number {
    if (note.NoteTie.Notes[0] == note) {
      return note.NoteTie.Duration.RealValue
    }
    return -1
  }

  // This differs from the above function in that it expects note data prerendered from the parsing lambda
  // private calculateTiedNoteLengthPrerendered(note: Tie): number {
  //     if (note.NoteTie.Notes[0] == note) {
  //         return note.NoteTie.Duration.RealValue
  //     }
  //     return -1
  // }

  private calculateCorrectNoteOffMin(noteDuration: number) {
    if (noteDuration <= 1.0 / 4.0) {
      return noteDuration * 0.5
    }
    // If duration is between a quarter note and half note, use this block.
    else if (noteDuration > 1.0 / 4.0 && noteDuration < 2.0 / 4.0) {
      const nRange = (2.0 / 4.0) - (1.0 / 4.0)
      // Scale from 0.5 to 0.625 of the note, on a nearly linear curve
      let nScale = 1.0 - ((2.0 / 4.0) - noteDuration) / nRange
      // nScale is normalized from 0..1; putting that on a curve nScale^0.6
      // lets a noteDuration=1.5 have minimum offset of just under 1 beat.
      // Adjust the following line to adjust how "aggressive" scaling between 1 and 2 is.
      // Use graphtoy.com to visualize the curve you're adjusting.
      nScale = Math.pow(nScale, 0.6)
      return (1.0 / 8.0) + (0.1875) * nScale
    } else {
      return noteDuration - (1.0 / 4.0) + (1.0 / 16.0)
    }
  }

  protected pulseMatchedNote(note: NoteEvent, colorClass: string, noteTimestamp?: number) {
    const vgNote = (note.Graphics[0] as unknown as { staveNote: { id: string } })
    // const staveNote = vgNote.getSVGGElement()
    let staveNote = document.getElementById(vgNote.staveNote.id)
    // Notes with staves will report the wrong height; we have to grab a descendant
    // which is ONLY the note head so we calculate pulse height offset correctly.
    const noteHead = staveNote?.querySelector('.vf-notehead')

    if (noteHead == null) {
      console.error("note has no note head!!")
      return
    }
    let colorClassCSSPostfix = 'correct-green'
    if (colorClass === ERROR_REC_CLASS.CORRECT && this.colorblindType === COLORBLIND_TYPE.RED_GREEN) {
      colorClassCSSPostfix = 'correct-blue'
    } else if (colorClass === ERROR_REC_CLASS.CORRECT && this.colorblindType === COLORBLIND_TYPE.FULL) {
      colorClassCSSPostfix = 'correct-white'
    }


    // Get the bounding rect of the note head relative to the document body.
    // We will append a pulse to the document, and position it over the note.
    const boundingRect = noteHead.getBoundingClientRect()

    // Create the pulse.
    const pulse = document.createElement("div")
    pulse.classList.add("pulsating-circle", colorClassCSSPostfix)
    document.body.appendChild(pulse)

    // Position the pulse.
    pulse.style.left = (boundingRect.x + boundingRect.width / 2) + "px"
    pulse.style.top = (window.scrollY + boundingRect.y + boundingRect.height / 2) + "px"

    // !!TODO AUSTIN!! You still need to track and delete the pulses!!!!!
  }

  private isOnsetTimeInBucket(timestamp: number, bucketIndex: number) {
    const minOnsetTimestamp = this.eventBuckets[bucketIndex].minOnsetTimestamp;
    const maxOnsetTimestamp = this.eventBuckets[bucketIndex].maxOnsetTimestamp;
    // logger.info("minOnsetTimestamp = " + minOnsetTimestamp)
    // logger.info("maxOnsetTimestamp = " + maxOnsetTimestamp )
    // logger.info("timestamp = " + timestamp)
    // logger.info( timestamp > minOnsetTimestamp
    //     && timestamp < maxOnsetTimestamp)
    return timestamp > minOnsetTimestamp
      && timestamp < maxOnsetTimestamp
  }

  private isInBucket(timestamp: number, bucketIndex: number) {
    const minOnsetTimestamp = this.eventBuckets[bucketIndex].minOnsetTimestamp;
    const maxOffsetTimestamp = this.eventBuckets[bucketIndex].maxOffsetTimestamp;
    // logger.info("minOnsetTimestamp = " + minOnsetTimestamp)
    // logger.info("maxOnsetTimestamp = " + maxOnsetTimestamp )
    // logger.info("timestamp = " + timestamp)
    // logger.info( timestamp > minOnsetTimestamp
    //     && timestamp < maxOnsetTimestamp)
    return timestamp > minOnsetTimestamp
      && timestamp < maxOffsetTimestamp
  }

  public clearErrors() {
    for (const eventBucket of this.eventBuckets) {
      eventBucket.notes.forEach(note => {
        resetNote(note.Graphics);
        note.OffsetState = MidiEventState.UNSET;
        note.OnsetState = MidiEventState.UNSET;
      });
    }
    this.accuracyQueue = [];
  }

  protected makeNoteSemiError = (
    graphics: PrerenderedGraphics[],
    colorblindType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => makeNoteSemiError(graphics, colorblindType);
  //protected this.makeNoteRed(graphics: PrerenderedGraphics[]) {}
  protected makeNoteCorrect = (
    graphics: PrerenderedGraphics[],
    colorblindType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => makeNoteCorrect(graphics, colorblindType);
  protected makeNoteError = (
    graphics: PrerenderedGraphics[],
    colorblindType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => makeNoteError(graphics, colorblindType);
}